import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Paper } from '@material-ui/core';
import { StickyContainer } from 'react-sticky';
import Button from '@material-ui/core/Button';
import PlanPackage from '../SubscriptionBundle/PlanPackage';
import { usePlanPackageForm } from '../../hooks/usePlanPackageForm';
import { useDispatch, useSelector } from 'react-redux';
import {
  currentEnrichedJobSelector,
  draftJobUpdated,
  fetchEnrichedJobById
} from '../../redux/features/job/jobsSlice';
import { useHistory, useLocation } from 'react-router-dom';
import { APP_PATH_DASHBOARD } from '../../config';

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: 0,
    padding: theme.spacing(4)
  },
  headerSectionView: {
    marginBottom: 10,
    padding: 20
  },
  stepperBtn: {
    textTransform: 'none',
    fontSize: '20px',
    marginRight: theme.spacing(3),
    marginTop: theme.spacing(3)
  },
  titleTopSection: {
    textAlign: 'center',
    marginBottom: 10,
    padding: 20
  }
}));

// Default job to show in preview when no job is selected from Redux
const defaultPreviewJob = {
  id: 'preview',
  title: 'Senior Full-Stack Developer',
  category: 'Software Development',
  type: 'Full-Time',
  description:
    'We are looking for an experienced Full-Stack Developer to join our remote team. You will work on cutting-edge technologies and help build scalable applications.',
  salaryFrom: 80000,
  salaryTo: 120000,
  currency: 'USD',
  location: 'Remote - Worldwide',
  regionalRestrictions: [],
  status: 'ACTIVE'
};

const defaultPreviewCompany = {
  id: 'preview',
  name: 'Your Company Name',
  description:
    'Leading technology company focused on innovation and excellence.',
  website: 'https://yourcompany.com'
};

const EditorPlanPackageView = () => {
  const classes = useStyles();
  const location = useLocation();
  const dispatch = useDispatch();
  const history = useHistory();

  // Extract jobId from URL query params
  const searchParams = new URLSearchParams(location.search);
  const jobIdFromUrl = searchParams.get('jobId');
  const bundleOnly = searchParams.get('bundle') === '1';
  const currentEnrichedJob = useSelector(currentEnrichedJobSelector);

  // Fetch job if jobId is in URL and we don't have it yet
  useEffect(() => {
    if (
      jobIdFromUrl &&
      (!currentEnrichedJob || currentEnrichedJob.job?.id !== jobIdFromUrl)
    ) {
      console.log(
        '📥 [EditorPlanPackageView] Fetching job from URL param:',
        jobIdFromUrl
      );
      dispatch(fetchEnrichedJobById({ jobId: jobIdFromUrl }));
    }
  }, [jobIdFromUrl, currentEnrichedJob, dispatch]);

  // Update draft job when enriched job is loaded
  useEffect(() => {
    if (jobIdFromUrl && currentEnrichedJob && currentEnrichedJob.job) {
      console.log(
        '💾 [EditorPlanPackageView] Setting draft job for preview:',
        currentEnrichedJob.job
      );
      dispatch(draftJobUpdated({ job: currentEnrichedJob.job }));
    }
  }, [jobIdFromUrl, currentEnrichedJob, dispatch]);

  const {
    onChangePlanSelectionCheckList,
    onChangePlanSelectionRadioList,
    onBrandColorChange,
    createSubscription,
    userEmail,
    draftJob,
    draftCompany,
    totalSelectedPlans,
    allSelectedPlanIDs,
    allSelectedPlans,
    brandColor
  } = usePlanPackageForm(bundleOnly ? null : jobIdFromUrl, {
    disableJobAssociation: bundleOnly
  });

  // Determine if we should use default preview data
  const hasValidJob = draftJob && draftJob.id && draftJob.title;
  const hasValidCompany = draftCompany && draftCompany.name;
  const jobForPreview = hasValidJob ? draftJob : defaultPreviewJob;
  const companyForPreview = hasValidCompany
    ? draftCompany
    : defaultPreviewCompany;

  // Log what job is being used for preview
  React.useEffect(() => {
    console.log('📊 [EditorPlanPackageView] Draft job:', draftJob);
    console.log('📊 [EditorPlanPackageView] Has valid job:', hasValidJob);
    console.log('📊 [EditorPlanPackageView] Will use job:', jobForPreview);
    console.log('📊 [EditorPlanPackageView] Draft company:', draftCompany);
    console.log(
      '📊 [EditorPlanPackageView] Has valid company:',
      hasValidCompany
    );
    console.log(
      '📊 [EditorPlanPackageView] Will use company:',
      companyForPreview
    );
  }, [
    draftJob,
    draftCompany,
    hasValidJob,
    hasValidCompany,
    jobForPreview,
    companyForPreview
  ]);

  const handleMakeChanges = () => {
    // Go back in history always with react router
    history.push(APP_PATH_DASHBOARD);
  };

  // Handle tier selection to update preview
  const handleTierSelected = (tier) => {
    console.log('🎨 [EditorPlanPackageView] Tier selected for preview:', tier);
  };

  return (
    <Paper className={classes.root}>
      <StickyContainer>
        <Grid container spacing={3} direction="row" justifyContent="center">
          <Grid item lg={6} md={6} xl={6} xs={12}>
            <PlanPackage
              totalSelectedPlans={totalSelectedPlans}
              allSelectedPlanIDs={allSelectedPlanIDs}
              allSelectedPlans={allSelectedPlans}
              email={userEmail}
              onChangePlanSelectionCheckList={onChangePlanSelectionCheckList}
              onChangePlanSelectionRadioList={onChangePlanSelectionRadioList}
              onBrandColorChange={onBrandColorChange}
              onTierSelected={handleTierSelected}
              createSubscription={createSubscription}
              jobId={bundleOnly ? null : draftJob?.id}
              jobRegionalRestrictions={draftJob?.regionalRestrictions}
              brandColor={brandColor}
              bundleOnly={bundleOnly}
            />
          </Grid>
        </Grid>
      </StickyContainer>
      <Button
        data-test="back-step-button-assistant"
        className={classes.stepperBtn}
        color="default"
        onClick={handleMakeChanges}
        variant="contained">
        I'll do it later
      </Button>
    </Paper>
  );
};

EditorPlanPackageView.defaultProps = {};

EditorPlanPackageView.propTypes = {};

export default EditorPlanPackageView;
